import { useEffect, useRef } from "react";
import { TIKTOKS, TIKTOK_SEARCH_URL } from "@/lib/constants";
import { ExternalLink } from "lucide-react";

// Loads TikTok embed script once and lets it hydrate <blockquote> elements.
const loadTikTokScript = () => {
  if (typeof window === "undefined") return;
  // Force re-process by removing old script and re-adding
  const existing = document.getElementById("tiktok-embed-script");
  if (existing) existing.remove();
  const s = document.createElement("script");
  s.id = "tiktok-embed-script";
  s.src = "https://www.tiktok.com/embed.js";
  s.async = true;
  document.body.appendChild(s);
};

export const TikTokWall = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    // small delay so React renders the blockquotes first
    const t = setTimeout(loadTikTokScript, 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      id="tiktok"
      data-testid="tiktok-section"
      className="relative z-10 px-4 sm:px-8 lg:px-12 py-16"
      ref={containerRef}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <span className="tag">CHAPTER 04</span>
          <span className="font-luckiest text-lg">THE EVIDENCE</span>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <h2
            className="font-bangers leading-[0.9] text-black"
            style={{ fontSize: "clamp(38px, 6vw, 84px)" }}
          >
            The proof is on{" "}
            <span style={{ color: "#0057FF", WebkitTextStroke: "2px #000" }}>
              the feed.
            </span>
          </h2>
          <a
            data-testid="tiktok-search-link"
            href={TIKTOK_SEARCH_URL}
            target="_blank"
            rel="noreferrer"
            className="btn-chunky btn-black text-sm self-start lg:self-auto"
          >
            SEARCH #DATBIHGAH <ExternalLink size={16} />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {TIKTOKS.map((t) => (
            <TikTokCard key={t.id} item={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

const TikTokCard = ({ item }) => {
  return (
    <div
      className="sticker p-3 flex flex-col gap-3"
      data-testid={`tiktok-card-${item.id}`}
    >
      <div className="flex items-center justify-between px-1">
        <span className="font-luckiest text-sm">@{item.user}</span>
        <span
          className="tag text-xs"
          style={{ background: "#0057FF", color: "#fff", border: "2px solid #000" }}
        >
          {item.likes} LIKES
        </span>
      </div>
      <div className="relative bg-white border-[3px] border-black overflow-hidden">
        <blockquote
          className="tiktok-embed"
          cite={item.url}
          data-video-id={item.id}
          style={{ maxWidth: 605, minWidth: 280 }}
        >
          <section>
            <a
              target="_blank"
              rel="noreferrer"
              title={`@${item.user}`}
              href={item.url}
            >
              @{item.user}
            </a>
          </section>
        </blockquote>
      </div>
      <a
        href={item.url}
        target="_blank"
        rel="noreferrer"
        className="font-luckiest text-xs underline underline-offset-2 self-start"
        data-testid={`tiktok-link-${item.id}`}
      >
        watch on tiktok →
      </a>
    </div>
  );
};
